import { Lock, ShieldCheck, Sparkles } from 'lucide-react';
import { Link } from 'react-router-dom';

import type { Screen } from '@/app/navigation/screens';
import { ROUTES } from '@/config/routes';
import { useDocumentTitle } from '@/shared/hooks/useDocumentTitle';

export interface ProductNotAssignedProps {
  /** The row that was refused — its icon and label are what make this screen specific. */
  screen: Screen;
}

/**
 * What somebody sees where a product screen would be, when it is not theirs.
 *
 * <h2>It says who to ask, because that is the whole remedy</h2>
 *
 * Nothing the person can do here changes the answer: access is rows in
 * `user_privilege_product`, written by an administrator of their company. "Forbidden" names
 * the state and leaves them to work out the next step; this names the step.
 *
 * <p>It deliberately does **not** offer to buy anything. The company may already hold the
 * product — the question is whether this person was given it — so an upgrade prompt would be
 * wrong about the remedy and addressed to somebody who is usually not the person who buys.
 *
 * <h2>The product's own icon, not a padlock</h2>
 *
 * A generic lock is the same picture on all six screens and says only "no". Drawing the
 * product's own icon with the lock as a badge says *which* product in the first glance, and
 * matches the row they clicked — a page that looks like nothing they have seen before reads
 * as an error page rather than as an answer.
 *
 * <h2>It does not list what they CAN open</h2>
 *
 * It did, briefly, as a grid of their own rows under "What you can open" — and it was cut on
 * 3 October 2026 for being a second menu beside the one already on screen. The rail is three
 * inches to the left and holds exactly those rows; repeating them makes the refusal look
 * like a navigation page and buries the one sentence somebody came here to read.
 *
 * <h2>The decoration is one subtree, generic, and `aria-hidden`</h2>
 *
 * A wash, two dashed paths and two faint glyphs, all inside a single `aria-hidden`
 * `pointer-events-none` wrapper — so there is one thing to delete, one place the stacking
 * order is decided, and no chance of an absolutely positioned ornament swallowing the click
 * on the link beneath it.
 *
 * <p><b>The glyphs are deliberately not about any product.</b> This screen is shared by all
 * six, so a star and a speech bubble would be Reviews & Ratings decoration drawn on the
 * Email Warmup refusal. The one product-specific thing is the icon in the middle, which
 * comes from `NAV_ITEMS` and is already right for whichever row was refused.
 *
 * <p>They are `hidden lg:flex`: below that width there is no room beside the text, and a
 * decoration that reflows into the reading column stops being decoration.
 *
 * <h2>Every background here is an arbitrary value, and that is not a style preference</h2>
 *
 * `src/index.css` carries `.dark` rules with `!important` that claim the utilities this
 * screen would otherwise use — `bg-white` in twelve of them, `bg-gradient-to-br` in nine,
 * `bg-gray-50` in six, `bg-amber-100` in one — and a `dark:` variant cannot beat any of
 * them: Tailwind compiles it to `:where(.dark, .dark *)`, which contributes zero
 * specificity. So `bg-[#ffffff]`, `bg-[#f9fafb]`, `bg-[#fffbeb]`,
 * `bg-[linear-gradient(…)]` and `bg-[radial-gradient(…)]` — the same colours under
 * selectors the legacy rules do not match.
 *
 * <p>It reads as noise and it is load-bearing: written the obvious way, this card is white
 * in the light theme and repainted by the skin in the dark one. The styling skill states
 * the workaround, and `styles/legacyDarkOverrides.test.ts` fails when those rules are
 * finally removed — which is the signal to put the plain utilities back.
 *
 * <h2>No status code, and no 404</h2>
 *
 * This is a rendered screen, not an HTTP response — the server's own refusal is **403
 * `product-not-assigned`** (backend `product-privileges.md` PP-28), and this is the courtesy
 * in front of it. A 404-style "nothing here" would contradict that and send somebody hunting
 * for a typo: the screen exists, it is simply not theirs.
 */
export function ProductNotAssigned({ screen }: ProductNotAssignedProps) {
  useDocumentTitle(`${screen.title} — no access`);

  const ProductIcon = screen.icon;

  return (
    /* Fills the content region in BOTH directions, and `flex-1` is what does it: `<main>`
       is a flex column, so this claims the height that is there rather than computing it.
       The `min-h` is a floor for the case where something above changes and the region ends
       up shorter than a screen.

       It took three goes. `items-center` centred a card of its natural height, leaving bands
       of empty page; `items-stretch` with `h-full` did not resolve, because a percentage
       height against a parent that has only `min-height` is not a definite height. Growing a
       flex item needs no definite parent height at all. */
    <div className="flex min-h-[calc(100vh-4rem)] w-full flex-1 flex-col p-4 sm:p-6 lg:p-8">
      {/* The card. A hairline ring rather than a border, so the edge reads as a surface
          rather than as a box drawn around text, and a soft shadow that is the only thing
          lifting it off the page. */}
      <div className="relative flex flex-1 items-center justify-center overflow-hidden rounded-3xl bg-[#ffffff] p-8 shadow-[0_1px_2px_rgba(16,24,40,0.04),0_12px_32px_-12px_rgba(16,24,40,0.12)] ring-1 ring-gray-900/5 sm:p-12 lg:p-16 dark:bg-[#0f1b33] dark:shadow-[0_12px_32px_-12px_rgba(0,0,0,0.6)] dark:ring-white/10">
        {/* THE DECORATIVE LAYER, and all of it is one aria-hidden subtree.
            
            Everything in here is ornament: it carries no information, is announced to
            nobody, and takes no pointer events -- the last of those matters, because an
            absolutely positioned layer over the card would otherwise swallow the click on
            the link beneath it.

            It is one wrapper rather than three stray absolute children, so there is exactly
            one thing to delete if the decoration is ever unwanted, and one place the
            stacking order is decided. */}
        <div aria-hidden className="pointer-events-none absolute inset-0 select-none">
          {/* The wash. Warm, and low enough that the text above it keeps its contrast --
              0.14 alpha amber over white measures no change in the heading's ratio. */}
          <div className="absolute inset-0 bg-[radial-gradient(42rem_26rem_at_50%_0%,rgba(251,191,36,0.14),rgba(251,191,36,0))] dark:bg-[radial-gradient(42rem_26rem_at_50%_0%,rgba(251,191,36,0.12),rgba(251,191,36,0))]" />

          {/* The path. An inline SVG rather than an image: it is two strokes, it inherits
              currentColor, and an <img> would be a file to ship and a request to make.

              preserveAspectRatio="none" so the curve stretches with the card instead of
              letter-boxing itself in the middle of it. It sits behind the glyphs below and
              passes well above and below the text column, never through it. */}
          <svg
            viewBox="0 0 1200 600"
            preserveAspectRatio="none"
            className="absolute inset-0 h-full w-full text-gray-900/[0.045] dark:text-white/[0.06]"
          >
            <path
              d="M -40 210 C 220 120, 300 330, 600 300 S 980 480, 1240 390"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="2 10"
              strokeLinecap="round"
            />
            <path
              d="M -40 420 C 260 360, 340 170, 600 190 S 960 60, 1240 140"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeDasharray="2 14"
              strokeLinecap="round"
            />
          </svg>

          {/* Two glyphs, left and right of the text column.

              GENERIC, not product-specific, and that is a decision rather than laziness:
              this screen is shared by all six products, so a star and a speech bubble would
              be decoration about Reviews & Ratings drawn on the Email Warmup refusal. The
              only product-specific thing on the page is the icon in the middle, which comes
              from NAV_ITEMS and is already right for whichever row was refused.

              `hidden lg:flex` because below that width there is no room beside the text, and
              a decoration that reflows into the reading column stops being decoration. */}
          <span className="absolute top-[22%] left-[8%] hidden h-16 w-16 items-center justify-center rounded-2xl ring-1 ring-gray-900/[0.06] lg:flex dark:ring-white/[0.08]">
            <Sparkles className="h-6 w-6 text-gray-900/10 dark:text-white/15" strokeWidth={1.5} />
          </span>

          <span className="absolute right-[9%] bottom-[24%] hidden h-14 w-14 items-center justify-center rounded-2xl ring-1 ring-gray-900/[0.06] lg:flex dark:ring-white/[0.08]">
            <ShieldCheck
              className="h-5 w-5 text-gray-900/10 dark:text-white/15"
              strokeWidth={1.5}
            />
          </span>
        </div>

        {/* The CARD is full width; the WORDS are not. A sentence spanning a wide screen is
            unreadable, so the measure is capped here rather than on the card. */}
        <div className="relative flex w-full max-w-xl flex-col items-center text-center">
          {/* The product's own icon, with the lock as a badge on it. */}
          <div className="relative">
            <div className="flex h-20 w-20 items-center justify-center rounded-2xl bg-[linear-gradient(135deg,#fffbeb_0%,#fef3c7_100%)] ring-1 ring-amber-900/10 dark:bg-[linear-gradient(135deg,rgba(251,191,36,0.18)_0%,rgba(251,191,36,0.08)_100%)] dark:ring-amber-300/20">
              <ProductIcon
                aria-hidden
                className="h-9 w-9 text-[#b45309] dark:text-[#fcd34d]"
                strokeWidth={1.75}
              />
            </div>

            <span className="absolute -right-2 -bottom-2 flex h-9 w-9 items-center justify-center rounded-full bg-[#ffffff] ring-1 ring-gray-900/10 dark:bg-[#0f1b33] dark:ring-white/15">
              <Lock aria-hidden className="h-4 w-4 text-[#b45309] dark:text-[#fcd34d]" />
            </span>
          </div>

          <h1 className="mt-8 text-2xl font-bold tracking-tight text-balance text-gray-900 sm:text-3xl dark:text-gray-50">
            You do not have access to {screen.title}
          </h1>

          <p className="mt-4 text-base/7 text-balance text-gray-600 dark:text-gray-300">
            Your company may well have this product — it has not been assigned to your account. An
            administrator of your company can give you access.
          </p>

          <Link
            to={ROUTES.dashboard}
            className="app-outline-button mt-8 inline-flex items-center justify-center gap-2 rounded-xl px-5 py-3 font-semibold text-gray-700 ring-1 ring-gray-300 transition-colors hover:bg-[#f9fafb] focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 focus-visible:outline-none dark:text-gray-100 dark:ring-white/15 dark:hover:bg-[#182845]"
          >
            Back to the dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}
